import { useMemo, useState } from "react";
import { Search, Filter, Eye, UserPlus, Download, X } from "lucide-react";
import { Button } from "@/projects/components/ui/button";
import { Input } from "@/projects/components/ui/input";
import { Label } from "@/projects/components/ui/label";
import { Checkbox } from "@/projects/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/projects/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/projects/components/ui/table";
import { StatusBadge } from "@/projects/components/status-badge";
import { VideoPlayerModal } from "@/projects/components/video-player-modal";
import { AssignTeacherModal } from "@/projects/components/assign-teacher-modal";
import { SubmissionDetailsDrawer } from "@/projects/components/submission-details-drawer";
import { useSubmissions, useTeachers } from "@/projects/lib/useStore";
import type { Submission, SubmissionStatus } from "@/projects/lib/store";
import { toast } from "sonner";

const STATUSES: (SubmissionStatus | "All")[] = ["All", "Pending", "Assigned for Verification", "Not Proper", "Ready for Reward"];

export function SubmissionsPage() {
  const submissions = useSubmissions();
  const teachers = useTeachers();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<SubmissionStatus | "All">("All");
  const [teacherId, setTeacherId] = useState<string>("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [videoOpen, setVideoOpen] = useState<Submission | null>(null);
  const [assignFor, setAssignFor] = useState<string[] | null>(null);
  const [detail, setDetail] = useState<Submission | null>(null);
  const [showFilters, setShowFilters] = useState(true);

  const filtered = useMemo(() => {
    return submissions.filter((s) => {
      if (status !== "All" && s.status !== status) return false;
      if (teacherId !== "all" && s.assignedTeacherId !== teacherId) return false;
      if (from && new Date(s.submittedAt) < new Date(from)) return false;
      if (to && new Date(s.submittedAt) > new Date(to + "T23:59:59")) return false;
      if (q) {
        const t = q.toLowerCase();
        return (
          s.studentName.toLowerCase().includes(t) ||
          s.studentId.toLowerCase().includes(t) ||
          s.videoTitle.toLowerCase().includes(t) ||
          s.id.toLowerCase().includes(t)
        );
      }
      return true;
    });
  }, [submissions, q, status, teacherId, from, to]);

  const toggleAll = () => {
    if (selected.length === filtered.length) setSelected([]);
    else setSelected(filtered.map((s) => s.id));
  };

  const chips: { key: string; label: string; clear: () => void }[] = [];
  if (q) chips.push({ key: "q", label: `Search: ${q}`, clear: () => setQ("") });
  if (status !== "All") chips.push({ key: "s", label: `Status: ${status}`, clear: () => setStatus("All") });
  if (teacherId !== "all") {
    const t = teachers.find((x) => x.id === teacherId);
    chips.push({ key: "t", label: `Teacher: ${t?.name ?? teacherId}`, clear: () => setTeacherId("all") });
  }
  if (from) chips.push({ key: "f", label: `From ${from}`, clear: () => setFrom("") });
  if (to) chips.push({ key: "tt", label: `To ${to}`, clear: () => setTo("") });

  const handleExport = (scope: "all" | "filtered" | "selected") => {
    const rows =
      scope === "all" ? submissions :
      scope === "selected" ? filtered.filter((s) => selected.includes(s.id)) :
      filtered;
    if (!rows.length) { toast.error("No records to export."); return; }
    const headers = ["Reference", "Student Name", "Student ID", "Video Title", "File Name", "File Size (MB)", "Submitted", "Assigned Teacher", "Assigned Date", "Status", "Teacher Remarks", "Verified Date"];
    const csv = [
      headers.join(","),
      ...rows.map((s) => [s.id, s.studentName, s.studentId, s.videoTitle, s.fileName, s.fileSizeMB, s.submittedAt, s.assignedTeacherName ?? "", s.assignedAt ?? "", s.status, (s.teacherRemarks ?? "").replace(/,/g, ";"), s.verifiedAt ?? ""].join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `submissions-${scope}-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Excel exported successfully");
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, ID, title, reference…" className="w-64 pl-8" />
          </div>
          <Button variant="outline" size="sm" onClick={() => setShowFilters((v) => !v)}>
            <Filter className="mr-2 h-4 w-4" /> Filters
          </Button>
        </div>
        <div className="flex items-center gap-2">
          {selected.length > 0 && (
            <Button size="sm" onClick={() => setAssignFor(selected)}>
              <UserPlus className="mr-2 h-4 w-4" /> Bulk Assign ({selected.length})
            </Button>
          )}
          <div className="relative">
            <Select onValueChange={(v) => handleExport(v as "all" | "filtered" | "selected")}>
              <SelectTrigger className="w-[170px]">
                <Download className="mr-2 h-4 w-4" />
                <SelectValue placeholder="Export to Excel" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All records</SelectItem>
                <SelectItem value="filtered">Filtered records</SelectItem>
                <SelectItem value="selected" disabled={selected.length === 0}>
                  Selected ({selected.length})
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {showFilters && (
        <div className="rounded-xl border bg-card p-4 shadow-soft">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as SubmissionStatus | "All")}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Assigned Teacher</Label>
              <Select value={teacherId} onValueChange={setTeacherId}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All teachers</SelectItem>
                  {teachers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">From Date</Label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">To Date</Label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
            <div className="flex items-end gap-2">
              <Button variant="outline" className="flex-1" onClick={() => { setQ(""); setStatus("All"); setTeacherId("all"); setFrom(""); setTo(""); }}>Reset</Button>
            </div>
          </div>
        </div>
      )}

      {chips.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {chips.map((c) => (
            <button key={c.key} onClick={c.clear} className="inline-flex items-center gap-1 rounded-full border bg-muted px-2.5 py-1 text-xs hover:bg-muted/70">
              {c.label} <X className="h-3 w-3" />
            </button>
          ))}
        </div>
      )}

      <div className="overflow-hidden rounded-xl border bg-card shadow-soft">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="w-10">
                  <Checkbox checked={selected.length === filtered.length && filtered.length > 0} onCheckedChange={toggleAll} />
                </TableHead>
                <TableHead>Reference</TableHead>
                <TableHead>Student</TableHead>
                <TableHead>Video Title</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead>Assigned</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="py-16 text-center">
                    <div className="mx-auto max-w-sm">
                      <div className="text-sm font-medium">No records match the selected filters.</div>
                      <div className="text-xs text-muted-foreground">Try adjusting or resetting your filters.</div>
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((s) => (
                <TableRow key={s.id} className="cursor-pointer hover:bg-muted/40" onClick={() => setDetail(s)}>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selected.includes(s.id)}
                      onCheckedChange={(v) => setSelected((prev) => (v ? [...prev, s.id] : prev.filter((x) => x !== s.id)))}
                    />
                  </TableCell>
                  <TableCell className="font-mono text-xs font-medium text-royal">{s.id}</TableCell>
                  <TableCell>
                    <div className="font-medium">{s.studentName}</div>
                    <div className="text-xs text-muted-foreground">{s.studentId}</div>
                  </TableCell>
                  <TableCell className="max-w-[220px]">
                    <div className="truncate font-medium">{s.videoTitle}</div>
                    <div className="text-xs text-muted-foreground">{s.fileSizeMB} MB · {s.fileName}</div>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{new Date(s.submittedAt).toLocaleDateString()}</TableCell>
                  <TableCell className="text-xs">
                    {s.assignedTeacherName ? (
                      <>
                        <div className="font-medium text-foreground">{s.assignedTeacherName}</div>
                        <div className="text-muted-foreground">{s.assignedAt && new Date(s.assignedAt).toLocaleDateString()}</div>
                      </>
                    ) : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell><StatusBadge status={s.status} /></TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setVideoOpen(s)}>
                        <Eye className="mr-1 h-3.5 w-3.5" /> View
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setAssignFor([s.id])}>
                        {s.assignedTeacherId ? "Reassign" : "Assign"}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-between border-t px-4 py-3 text-xs text-muted-foreground">
          <div>Showing {filtered.length} of {submissions.length} submissions</div>
        </div>
      </div>

      <VideoPlayerModal submission={videoOpen} open={!!videoOpen} onOpenChange={(o) => !o && setVideoOpen(null)} />
      <AssignTeacherModal
        submissionIds={assignFor}
        open={!!assignFor}
        onClose={() => { setAssignFor(null); setSelected([]); }}
      />
      <SubmissionDetailsDrawer submission={detail} open={!!detail} onClose={() => setDetail(null)} />
    </div>
  );
}
